import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusStamp } from "@/components/StatusStamp";
import { DocumentDetailModal } from "@/components/DocumentDetailModal";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { FileText, CheckCircle2, Clock, XCircle, Wallet, PlusCircle, Inbox, Send, BarChart3, Building2 } from "lucide-react";

const DIRECTION_LABEL = { incoming: "Входящий", outgoing: "Исходящий", internal: "Внутренний" };

function fmtTime(ts) {
  return ts ? ts.slice(0, 16).replace("T", " ") : "";
}

export default function Dashboard() {
  const { user, organization } = useAuth();
  const navigate = useNavigate();
  const { openNewDocument } = useOutletContext() || {};
  const [data, setData] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const load = () => {
    api.get("/dashboard/summary").then(({ data }) => setData(data)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const kpis = data
    ? [
        { label: "Всего документов", value: data.total_documents, icon: FileText, color: "text-slate-900" },
        { label: "Подписано", value: `${data.signed_percent}%`, icon: CheckCircle2, color: "text-blue-600" },
        { label: "На подписи", value: data.in_progress_count, icon: Clock, color: "text-emerald-600" },
        { label: "Отказано", value: data.rejected_count, icon: XCircle, color: "text-rose-600" },
        { label: "Экономия", value: `${data.estimated_savings_rub?.toLocaleString("ru-RU")} ₽`, icon: Wallet, color: "text-amber-600" },
      ]
    : [];

  const quickActions = [
    { label: "Новый документ", icon: PlusCircle, action: () => openNewDocument?.(), testid: "dashboard-quick-new-document" },
    { label: "Входящие", icon: Inbox, action: () => navigate("/documents/incoming"), testid: "dashboard-quick-incoming" },
    { label: "Исходящие", icon: Send, action: () => navigate("/documents/outgoing"), testid: "dashboard-quick-outgoing" },
    { label: "Отчёты", icon: BarChart3, action: () => navigate("/reports"), testid: "dashboard-quick-reports" },
    { label: "Контрагенты", icon: Building2, action: () => navigate("/counterparties"), testid: "dashboard-quick-counterparties" },
  ];

  return (
    <div data-testid="dashboard-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900" data-testid="dashboard-welcome-heading">
          Добро пожаловать, {user?.name?.split(" ")[0] || ""}
        </h1>
        <p className="text-sm text-slate-500 mt-1" data-testid="dashboard-org-name">
          {organization?.name || data?.organization_name || ""}
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="dashboard-quick-actions">
        {quickActions.map((qa) => (
          <button
            key={qa.testid}
            onClick={qa.action}
            data-testid={qa.testid}
            className="flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg border border-slate-200 bg-white hover:border-slate-900 hover:bg-slate-900 hover:text-white transition-colors"
          >
            <qa.icon className="w-4 h-4" /> {qa.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="dashboard-kpi-summary">
        {kpis.map((k, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-xl p-4">
            <k.icon className={`w-5 h-5 mb-2 ${k.color}`} />
            <div className="text-xl font-bold text-slate-900">{k.value}</div>
            <div className="text-xs text-slate-500 mt-0.5">{k.label}</div>
          </div>
        ))}
        {!data && <div className="col-span-5 text-center text-slate-400 text-sm py-10">Загрузка показателей...</div>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-4" data-testid="dashboard-dynamics-chart">
          <div className="text-sm font-semibold text-slate-700 mb-3">Динамика за 14 дней</div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data?.dynamics || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="signed" stroke="#4E9BEE" strokeWidth={2} name="Подписано" dot={false} />
              <Line type="monotone" dataKey="in_progress" stroke="#15803D" strokeWidth={2} name="На подписи" dot={false} />
              <Line type="monotone" dataKey="rejected" stroke="#B91C1C" strokeWidth={2} name="Отказано" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="dashboard-top-counterparties">
          <div className="text-sm font-semibold text-slate-700 mb-3">Активные контрагенты</div>
          <div className="space-y-2">
            {(data?.top_counterparties || []).map((cp, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-slate-700 truncate pr-2">{cp.name}</span>
                <span className="text-xs text-slate-500 shrink-0">{cp.signed}/{cp.total}</span>
              </div>
            ))}
            {data && data.top_counterparties.length === 0 && (
              <div className="text-center text-slate-400 text-xs py-6">Нет данных по контрагентам</div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="dashboard-recent-documents">
          <div className="text-sm font-semibold text-slate-700 mb-3">Последние документы</div>
          <div className="space-y-1.5">
            {(data?.recent_documents || []).map((doc) => (
              <div
                key={doc.id}
                onClick={() => setSelectedDoc(doc)}
                data-testid={`dashboard-doc-row-${doc.id}`}
                className="flex items-center justify-between gap-2 text-sm bg-slate-50 rounded-lg px-3 py-2 cursor-pointer hover:bg-slate-100"
              >
                <div className="min-w-0">
                  <div className="text-slate-800 truncate">{doc.type} №{doc.number}</div>
                  <div className="text-xs text-slate-400 truncate">
                    {DIRECTION_LABEL[doc.direction]}{doc.counterparty_name ? ` · ${doc.counterparty_name}` : ""}
                  </div>
                </div>
                <StatusStamp status={doc.status} className="shrink-0" />
              </div>
            ))}
            {data && data.recent_documents.length === 0 && (
              <div className="text-center text-slate-400 text-xs py-6">Документов пока нет</div>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="dashboard-recent-activity">
          <div className="text-sm font-semibold text-slate-700 mb-3">Журнал действий</div>
          <div className="space-y-2">
            {(data?.recent_activity || []).map((a) => (
              <div key={a.id} className="text-sm border-b border-slate-50 pb-2 last:border-0">
                <div className="text-slate-700">
                  <span className="font-medium">{a.user_name}</span>
                  {a.position_label && <span className="text-slate-400"> ({a.position_label})</span>} — {a.action_label}
                  {a.target_label && <span className="text-slate-500"> ({a.target_label})</span>}
                </div>
                <div className="text-xs text-slate-400">{fmtTime(a.created_at)}</div>
              </div>
            ))}
            {data && data.recent_activity.length === 0 && (
              <div className="text-center text-slate-400 text-xs py-6">Действий пока нет</div>
            )}
          </div>
        </div>
      </div>

      {selectedDoc && (
        <DocumentDetailModal
          docId={selectedDoc.id}
          open={!!selectedDoc}
          onOpenChange={(v) => !v && setSelectedDoc(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
